import React from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import moment from "moment";
import styled, { withTheme } from "styled-components";
import { Button } from "@vitalsource/vstui";
import { t, Translation } from "../../../../core/translate";
import { CheckmarkCircle, Warning, Stopwatch } from "@vitalsource/vstui-icons";
import { cloud, grass, cherry } from "@vitalsource/vstui/colors";
import { selectors as bridgeSelectors } from "../../../../../redux/bridge";
import * as bridgeActions from "../../../../../redux/bridge/actions";
import { selectors as brandSelectors } from "../../../../../redux/brand";
import LibraryHelpers from "../../../../../stores/library-helpers";
import { actions as uiLibraryActions } from "../../../../../redux/ui/library";
import { actions as bookActions } from "../../../../../redux/book";
import { selectors as routerSelectors } from "../../../../../redux/router";
import { actions as modalActions } from "../../../../../redux/modals";

const AvailabilityContainer = styled.div`
  position: relative;
  background: white;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.2);
  border-radius: 4px;
  font-size: 1.4rem;
  ${props =>
    props.copies ? `padding: 0 32px 24px 32px;` : `padding: 24px 32px;`};
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 365px;
  margin: 15px auto 14px;
`;
const UnavailableContainer = styled.div`
  margin: 0 3px 16px 0;
  background: white;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.2);
  border-radius: 4px;
`;
const AvailabilityLabel = styled.div`
  display: flex;
  flex-direction: column;
  line-height: 1.1;
  margin-top: -15px;
  font-size: 2.4rem;
  svg {
    margin: 0 auto 5px;
    display: block;
    width: 29px;
    height: 29px;
  }
`;
const CopiesContainer = styled.div`
  color: ${cloud.getShade(6)};
  font-style: italic;
  font-size: 1.2rem;
  margin: 16px 0 10px 0;
`;
const IconText = styled.div`
  display: flex;
  align-items: center;
`;
const LoanMessage = styled.div`
  color: ${cloud.getShade(9)};
  font-size: 1.6rem;
  display: inline-block;
  ${props => {
    return props.addPadding ? `padding-top: 24px;` : `padding-top: 0;`;
  }}
  ${props =>
    props.hasButton ? `padding-bottom: 16px;` : `padding-bottom: 0;`};
  svg {
    margin-right: 7.2px;
    height: 14px;
    width: 14px;
    vertical-align: middle;
  }
`;
const UnavailableText = styled.div`
  background-color: white;
  color: ${cloud.getShade(6)};
  border-radius: 4px;
  font-size: 1.6rem;
  font-style: italic;
  width: 100%;
  text-align: center;
  padding: 16px 35px;
`;
const UnavailableHeader = styled.div`
  display: flex;
  border-radius: 4px 4px 0 0;
  background-color: ${cloud.getShade(6)};
  font-size: 16px;
  font-weight: 500;
  width: 100%;
  height: 32px;
  color: white;
  justify-content: center;
  align-items: center;
`;
const ReturnMessage = styled.div`
  color: ${cloud.getShade(6)};
  font-size: 1.2rem;
  margin-top: 8px;
  line-height: 1;
  text-align: center;
  white-space: normal;
`;
const ReturnLink = styled(Button)`
  color: ${cherry.getShade(2)};
  text-decoration: underline;
  padding-left: 0px;
  padding-right: 0px;
`;
const BorrowTotal = styled.div`
  padding-top: 8px;
  font-size: 1.6rem;
  font-weight: 400;
`;
class AvailabilityMessage extends React.Component {
  render() {
    const {
      asset,
      isActive,
      isReturnable,
      assetExpiringSoon,
      isCreditAsset,
      isConcurrencyAsset,
      hasAvailableCopies,
      hasUnlimitedAccess,
      hasAvailableUserCreditsForAsset,
      availableCopiesOfAssetFromAllSubscriptions,
      firstCreditForAsset,
      totalCopiesOfAssetFromAllSubscriptions,
      isAvailableToBorrow,
      termsDuration,
      termsAbsDate,
      expirationTimestamp,
      showCopiesEnabled,
      launchBorrowedBook,
      native,
      borrowBook,
      closePanel,
      returnBook,
      distributable,
      platform
    } = this.props;
    if (!asset) {
      // it isn't a bridge book, no need to show messaging
      return null;
    }
    let mainButton = "";
    let unavailableText = "";
    let returnMessage = "";
    let returnLink = "";
    let loanMessage = "";
    const borrowBookClick = () => {
      borrowBook()
        .then(() => {
          if (native && platform !== "web") {
            launchBorrowedBook();
          } else {
            closePanel();
            LibraryHelpers.borrowBookToast(asset, launchBorrowedBook);
          }
        })
        .catch(() => {
          return null;
        });
    };

    if (!hasUnlimitedAccess && isAvailableToBorrow && !isActive) {
      mainButton = (
        <Button onClick={borrowBookClick} fullWidth>
          {t("library.borrow")}
        </Button>
      );
      if (isReturnable) {
        returnMessage = (
          <ReturnMessage>{t("explore.returns-available")}</ReturnMessage>
        );
      } else {
        returnMessage = (
          <ReturnMessage>{t("explore.non-returnable")}</ReturnMessage>
        );
      }
    }

    if (hasAvailableCopies && !hasAvailableUserCreditsForAsset && !isActive) {
      mainButton = null;
      unavailableText = (
        <UnavailableText>
          {t("library.borrow-limit-reached")}
          <BorrowTotal>
            <Translation
              id="library.borrow-item-limit-count"
              total={firstCreditForAsset.credits.total}
            />
          </BorrowTotal>
        </UnavailableText>
      );
      if (isReturnable) {
        returnLink = (
          <ReturnLink
            naked
            fullWidth
            onClick={() => {
              this.props.closePanel();
              this.props.openModal("CreditsModal");
            }}
          >
            {t("library.book.return-another-item")}
          </ReturnLink>
        );
      }
    }

    if (!isActive && !hasAvailableCopies && hasAvailableUserCreditsForAsset) {
      mainButton = null;
      unavailableText = (
        <UnavailableText>{t("library.book.all-copies-in-use")}</UnavailableText>
      );
    }

    if (isActive && isReturnable) {
      mainButton = (
        <Button
          onClick={() => {
            returnBook();
            LibraryHelpers.returnBookToast(asset.title, asset.vbid);
          }}
          fullWidth
        >
          {t("library.return-book")}
        </Button>
      );
      returnMessage = (
        <ReturnMessage>{t("explore.item-will-return")}</ReturnMessage>
      );
    }

    if (termsDuration) {
      if (termsDuration < 24) {
        loanMessage = (
          <IconText>
            <Stopwatch color={cloud.getShade(9)} />
            <Translation
              id="library.book.loan-period-hours"
              hours={termsDuration}
            />
          </IconText>
        );
      } else {
        loanMessage = (
          <IconText>
            <Stopwatch color={cloud.getShade(9)} />
            <Translation
              id="library.book.loan-period-days"
              days={parseInt(termsDuration / 24)}
            />
          </IconText>
        );
      }
    }
    if (termsAbsDate) {
      loanMessage = (
        <IconText>
          <Stopwatch color={cloud.getShade(9)} />
          <Translation
            id="library.book.loan-period-end-date"
            date={termsAbsDate}
          />
        </IconText>
      );
    }

    if (expirationTimestamp && assetExpiringSoon) {
      const duration = moment.duration(
        new Date(expirationTimestamp) - new Date(),
        "milliseconds"
      );
      const days = parseInt(duration.asDays());
      if (days === 0) {
        loanMessage = (
          <IconText>
            <Stopwatch color={cherry.getShade(2)} />
            <Translation id="library.loan-expires-less-day" />
          </IconText>
        );
      } else {
        loanMessage = (
          <IconText>
            <Stopwatch color={cloud.getShade(9)} />
            <Translation id="library.loan-expiry-days" days={days} />
          </IconText>
        );
      }
    }

    let copiesMessage = "";
    if (showCopiesEnabled && !isActive) {
      if (
        totalCopiesOfAssetFromAllSubscriptions &&
        totalCopiesOfAssetFromAllSubscriptions !== "unlimited"
      ) {
        copiesMessage = (
          <CopiesContainer>
            <Translation
              id="library.book.copies-left"
              available={availableCopiesOfAssetFromAllSubscriptions}
              total={totalCopiesOfAssetFromAllSubscriptions}
            />
          </CopiesContainer>
        );
      }
    }

    let availabilityLabel = "";
    if (showCopiesEnabled && !isActive) {
      // Below will need to change && once updated on the bridge api
      if (isCreditAsset || isConcurrencyAsset) {
        if (isAvailableToBorrow) {
          availabilityLabel = (
            <AvailabilityLabel>
              <CheckmarkCircle color={grass.getShade(2)} />
              <span style={{ color: grass.getShade(2) }}>
                {t("library.book.available")}
              </span>
            </AvailabilityLabel>
          );
        } else {
          availabilityLabel = (
            <AvailabilityLabel>
              <Warning color={cherry.getShade(2)} />
              <span style={{ color: cherry.getShade(2) }}>
                {t("library.book.unavailable")}
              </span>
            </AvailabilityLabel>
          );
        }
      }
    }
    return !distributable & !isActive ? (
      <UnavailableContainer>
        <UnavailableHeader>{t("library.book.unavailable")}</UnavailableHeader>
        <UnavailableText>
          {t("library.book.title-has-been-taken-out-of-distribution")}
        </UnavailableText>
      </UnavailableContainer>
    ) : (
      (isReturnable || assetExpiringSoon || !isActive) && !hasUnlimitedAccess && (
        <AvailabilityContainer copies={showCopiesEnabled && isConcurrencyAsset}>
          {availabilityLabel}
          {copiesMessage}
          {
            <LoanMessage
              hasButton={mainButton}
              addPadding={
                !(hasUnlimitedAccess && isAvailableToBorrow && !isActive)
              }
            >
              {loanMessage}
            </LoanMessage>
          }
          {mainButton}
          {returnMessage}
          {unavailableText}
          {returnLink}
        </AvailabilityContainer>
      )
    );
  }
}

AvailabilityMessage.propTypes = {
  isbn: PropTypes.string.isRequired
};

const mapStateToProps = (state, props) => ({
  asset: bridgeSelectors.getAsset(state, props.isbn),
  isActive: bridgeSelectors.isActive(state, props.isbn),
  isReturnable: bridgeSelectors.isReturnable(state, props.isbn),
  assetExpiringSoon: bridgeSelectors.assetExpiringSoon(state, props.isbn),
  isConcurrencyAsset: bridgeSelectors.isConcurrencyAsset(state, props.isbn),
  isCreditAsset: bridgeSelectors.isCreditAsset(state, props.isbn),
  hasAvailableCopies: bridgeSelectors.hasAvailableCopies(state, props.isbn),
  hasUnlimitedAccess: bridgeSelectors.hasUnlimitedAccess(state, props.isbn),
  availableUserCreditCount: bridgeSelectors.getAvailableUserCreditCount(
    state,
    props.isbn
  ),
  hasAvailableUserCreditsForAsset: bridgeSelectors.hasAvailableUserCreditsForAsset(
    state,
    props.isbn
  ),
  isAvailableToBorrow: bridgeSelectors.isAvailableToBorrow(state, props.isbn),
  availableCopiesOfAssetFromAllSubscriptions: bridgeSelectors.getAvailableCopiesFromAllSubscriptions(
    state,
    props.isbn
  ),
  firstCreditForAsset: bridgeSelectors.getFirstCreditForAsset(
    state,
    props.isbn
  ),
  totalCopiesOfAssetFromAllSubscriptions: bridgeSelectors.getTotalCopiesFromAllSubscriptions(
    state,
    props.isbn
  ),
  termsDuration: bridgeSelectors.getTermsDuration(state, props.isbn),
  termsAbsDate: bridgeSelectors.getTermsAbsDate(state, props.isbn),
  expirationTimestamp: bridgeSelectors.getExpirationTimestamp(
    state,
    props.isbn
  ),
  showCopiesEnabled: brandSelectors.getBrandValue(
    state,
    "Subscriptions.assets.showCopies",
    true
  ),
  native: routerSelectors.isNative(state),
  distributable: bridgeSelectors.isDistributable(state, props.isbn),
  platform: routerSelectors.getQueryParam(state, "platform")
});

const mapDispatchToProps = (dispatch, props) => ({
  borrowBook: () => dispatch(bridgeActions.activateAsset(props.isbn)),
  returnBook: () => dispatch(bridgeActions.returnAsset(props.isbn)),
  closePanel: () => dispatch(uiLibraryActions.closePanel()),
  launchBorrowedBook: () =>
    dispatch(bookActions.launchLicensedBook(props.isbn)),
  openModal: (modal, props) => dispatch(modalActions.openModal(modal, props))
});

export default withTheme(
  connect(mapStateToProps, mapDispatchToProps)(AvailabilityMessage)
);
